import type { TipoCampo } from '@qsms/shared'
import { CHAVES, gravarAjuste, lerAjuste, type ResponsavelNc } from './banco.js'

/// Cliente HTTP. Tudo que sai do app passa por aqui, e tudo aqui assume que a rede
/// pode não existir.

export interface Sessao {
  token: string
  expiraEm: string
  usuario: {
    id: string
    email: string
    nome: string
    perfis: string[]
    admin: boolean
    superAdmin: boolean
    vetodasObras: boolean
  }
}

export type SituacaoPlano = 'Concluído' | 'Concluído com atraso' | 'Atrasado' | 'Em andamento' | 'Sem prazo'

/// Uma inspeção do INI no painel: só o que os gráficos usam.
export interface LinhaPainel {
  id: string
  codigo: string
  obraId: string
  /// aaaa-mm-dd
  data: string | null
  situacao: string
  criticidadeApia: string | null
  titulo: string | null
  tipo: string | null
  empresa: string[]
  tecnico: string | null
  gerente: string | null
  encarregado: string | null
  tipologia: string | null
  criticidade: string | null
  prazo: string | null
  adequacao: string | null
  plano: SituacaoPlano
}

export interface DadosPainel {
  hoje: string
  obras: { id: string; codigo: string; nome: string }[]
  linhas: LinhaPainel[]
}

export class SemRede extends Error {
  constructor() {
    super('Sem conexão com o servidor.')
  }
}

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly corpo?: unknown,
  ) {
    super(mensagem)
  }
}

const BASE = import.meta.env.VITE_API_BASE ?? '/api'

export async function sessaoAtual(): Promise<Sessao | null> {
  return lerAjuste<Sessao | null>(CHAVES.sessao, null)
}

export async function guardarSessao(sessao: Sessao): Promise<void> {
  await gravarAjuste(CHAVES.sessao, sessao)
}

async function chamar<T>(
  caminho: string,
  opcoes: RequestInit & { semAuth?: boolean } = {},
): Promise<T> {
  const { semAuth, ...init } = opcoes
  const cabecalhos = new Headers(init.headers)

  if (!semAuth) {
    const sessao = await sessaoAtual()
    if (!sessao) throw new ErroApi(401, 'Sessão não iniciada.')
    cabecalhos.set('Authorization', `Dispositivo ${sessao.token}`)
  }
  if (init.body && !(init.body instanceof FormData) && !cabecalhos.has('Content-Type')) {
    cabecalhos.set('Content-Type', 'application/json')
  }

  let resposta: Response
  try {
    resposta = await fetch(`${BASE}${caminho}`, { ...init, headers: cabecalhos })
  } catch {
    // fetch só rejeita por falha de rede; qualquer status HTTP chega como resposta.
    throw new SemRede()
  }

  if (resposta.status === 204) return undefined as T
  const texto = await resposta.text()
  const corpo = texto ? (JSON.parse(texto) as unknown) : null

  if (!resposta.ok) {
    const mensagem =
      (corpo as { erro?: string } | null)?.erro ?? `Erro ${resposta.status} do servidor.`
    throw new ErroApi(resposta.status, mensagem, corpo)
  }
  return corpo as T
}

export const api = {
  saude: () => chamar<{ ok: boolean; versao: string }>('/saude', { semAuth: true }),

  entrar: (tokenEntra: string, dispositivo: string) =>
    chamar<Sessao>('/sessao/entrar', {
      method: 'POST',
      semAuth: true,
      headers: { Authorization: `Bearer ${tokenEntra}` },
      body: JSON.stringify({ dispositivo }),
    }),

  /// Só existe em desenvolvimento: o servidor aceita a requisição sem credencial
  /// quando LOGIN_DEV está ligado, o que config.ts proíbe em produção.
  entrarDev: (dispositivo: string) =>
    chamar<Sessao>('/sessao/entrar', {
      method: 'POST',
      semAuth: true,
      body: JSON.stringify({ dispositivo }),
    }),

  eu: () => chamar<Sessao['usuario'] & { obras: unknown[] }>('/sessao/eu'),

  cadastros: (desde?: string) =>
    chamar<{
      ate: string
      completo: boolean
      checklists: unknown[]
      obras: unknown[]
      pessoas: { email: string; nome: string }[]
      removidos: { obras: string[]; checklists: string[] }
      /// Ausente em servidor anterior a 25/09/2026.
      listas?: Record<string, string[]>
      /// Ausente em servidor anterior a 29/09/2026.
      responsaveisNc?: ResponsavelNc[]
    }>(`/cadastros${desde ? `?desde=${encodeURIComponent(desde)}` : ''}`),

  painel: () => chamar<DadosPainel>('/painel'),

  enviarInspecoes: (inspecoes: unknown[]) =>
    chamar<{ resultados: unknown[] }>('/sincronizacao/inspecoes', {
      method: 'POST',
      body: JSON.stringify({ inspecoes }),
    }),

  baixarInspecoes: (desde?: string) =>
    chamar<{ ate: string; inspecoes: unknown[]; temMais?: boolean }>(
      `/sincronizacao/inspecoes${desde ? `?desde=${encodeURIComponent(desde)}` : ''}`,
    ),

  verificarArquivos: (ids: string[]) =>
    chamar<{ presentes: string[]; faltando: string[] }>('/arquivos/verificar', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  enviarArquivo: async (id: string, blob: Blob, nome: string) => {
    const form = new FormData()
    form.append('arquivo', blob, nome)
    return chamar<{ id: string; jaExistia: boolean }>(`/arquivos/${id}`, {
      method: 'POST',
      body: form,
    })
  },

  urlArquivo: (id: string) => `${BASE}/arquivos/${id}`,

  inspecoes: (params: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') q.set(k, String(v))
    }
    return chamar<{ total: number; pagina: number; itens: unknown[] }>(`/inspecoes?${q}`)
  },

  inspecao: (id: string) => chamar<unknown>(`/inspecoes/${id}`),

  revisarCriticidade: (id: string, criticidadeApia: string, versaoBase: number) =>
    chamar<{ id: string; versao: number }>(`/inspecoes/${id}/criticidade-apia`, {
      method: 'PATCH',
      body: JSON.stringify({ criticidadeApia, versaoBase }),
    }),

  excluirInspecao: (id: string) => chamar<{ ok: true }>(`/inspecoes/${id}`, { method: 'DELETE' }),

  // ------------------------------------------------------------ administração
  //
  // Só admin, e só com rede: são consultas sobre o banco inteiro, que não tem
  // cópia no aparelho.

  adminUsuarios: (params: Record<string, string | number | boolean | undefined>) =>
    chamar<RespostaUsuarios>(`/admin/usuarios?${montarQuery(params)}`),

  adminUsuariosResumo: () => chamar<ResumoUsuarios>('/admin/usuarios/resumo'),

  adminUsuario: (id: string) => chamar<UsuarioDetalhe>(`/admin/usuarios/${id}`),

  adminAuditoria: (params: Record<string, string | undefined>) =>
    chamar<RespostaAuditoria>(`/admin/auditoria?${montarQuery(params)}`),

  adminAcessos: (params: Record<string, string | number | undefined>) =>
    chamar<RespostaAcessos>(`/admin/acessos?${montarQuery(params)}`),

  adminAcoes: (antesDe?: string) =>
    chamar<RespostaAcoesAdmin>(`/admin/acoes${antesDe ? `?antesDe=${antesDe}` : ''}`),

  // --- escrita (só admin) ---

  adminAlterarUsuario: (id: string, dados: { acessoLiberado?: boolean; ativo?: boolean; motivo?: string }) =>
    chamar<UsuarioLinha>(`/admin/usuarios/${id}`, { method: 'PATCH', body: JSON.stringify(dados) }),

  adminCriarUsuario: (dados: {
    nome: string
    email: string
    entraOid?: string
    perfis: PerfilNome[]
    obraIds: string[]
    motivo?: string
  }) =>
    chamar<{ id: string; nome: string; email: string }>('/admin/usuarios', {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  adminChecklists: () => chamar<ChecklistAdmin[]>('/admin/checklists'),

  adminChecklist: (id: string) => chamar<ChecklistDetalheAdmin>(`/admin/checklists/${id}`),

  adminCatalogo: () => chamar<CampoCatalogoAdmin[]>('/admin/checklists/catalogo'),

  adminCriarChecklist: (dados: CorpoChecklist) =>
    chamar<{ id: string; nome: string }>('/admin/checklists', { method: 'POST', body: JSON.stringify(dados) }),

  adminSalvarChecklist: (id: string, dados: CorpoChecklist) =>
    chamar<{ id: string; nome: string }>(`/admin/checklists/${id}`, { method: 'PUT', body: JSON.stringify(dados) }),

  adminAtivarChecklist: (id: string, ativo: boolean, motivo?: string) =>
    chamar<{ id: string; ativo: boolean; jaEstavaAssim: boolean }>(`/admin/checklists/${id}/ativo`, {
      method: 'PUT',
      body: JSON.stringify({ ativo, motivo }),
    }),

  adminDefinirObras: (id: string, obraIds: string[], motivo?: string) =>
    chamar<{ obraIds: string[] }>(`/admin/usuarios/${id}/obras`, {
      method: 'PUT',
      body: JSON.stringify({ obraIds, motivo }),
    }),

  adminSeguirRm: (id: string, dados: { perfis?: boolean; obras?: boolean }) =>
    chamar<{ perfisManuais: boolean; obrasManuais: boolean }>(`/admin/usuarios/${id}/seguir-rm`, {
      method: 'PUT',
      body: JSON.stringify(dados),
    }),

  adminIntegracaoRm: () => chamar<EstadoIntegracaoRm>('/admin/integracao-rm'),

  adminAplicarIntegracaoRm: (execucaoId: string) =>
    chamar<{ totais: TotaisRm; avisos: string[] }>(`/admin/integracao-rm/${execucaoId}/aplicar`, {
      method: 'POST',
      body: '{}',
    }),

  adminLerRmAgora: () =>
    chamar<{ resultado: 'mudou' | 'sem_mudanca'; total: number; aplicada?: boolean }>('/admin/integracao-rm/ler-agora', {
      method: 'POST',
      body: '{}',
    }),

  adminModoIntegracaoRm: (modo: 'previa' | 'automatico', motivo?: string) =>
    chamar<{ modo: string }>('/admin/integracao-rm/modo', {
      method: 'PUT',
      body: JSON.stringify({ modo, motivo }),
    }),

  adminDefinirPerfis: (id: string, perfis: PerfilNome[], motivo?: string) =>
    chamar<{ perfis: PerfilNome[] }>(`/admin/usuarios/${id}/perfis`, {
      method: 'PUT',
      body: JSON.stringify({ perfis, motivo }),
    }),

  adminPreviaExclusao: (id: string) =>
    chamar<PreviaExclusao>(`/admin/usuarios/${id}/exclusao`),

  adminExcluirUsuario: (id: string, motivo?: string) =>
    chamar<ResultadoExclusao>(`/admin/usuarios/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ motivo }),
    }),

  adminSimularLimpeza: () =>
    chamar<{ total: number; amostra: { id: string; nome: string; email: string }[]; aviso: string }>(
      '/admin/usuarios/limpar-sem-uso',
      { method: 'POST', body: JSON.stringify({ simular: true }) },
    ),

  adminLimpar: (confirmarTotal: number, motivo?: string) =>
    chamar<{ resultado: 'excluido'; total: number }>('/admin/usuarios/limpar-sem-uso', {
      method: 'POST',
      body: JSON.stringify({ simular: false, confirmarTotal, motivo }),
    }),
}

export interface PreviaExclusao {
  id: string
  nome: string
  email: string
  podeExcluir: boolean
  inspecoes: number
  revisoes: number
  auditorias: number
  acoes: number
  dispositivos: number
  /// Ausente em servidor anterior a 29/09/2026.
  naoConformidades?: number
}

export type ResultadoExclusao =
  | { resultado: 'excluido' }
  | {
      resultado: 'desativado'
      jaEstavaAssim: boolean
      explicacao: string
      vinculos: Omit<PreviaExclusao, 'id' | 'nome' | 'email'>
    }

// ---------------------------------------------------------------- checklists

export interface ChecklistAdmin {
  id: string
  codigo: string | null
  nome: string
  categoria: string | null
  revisao: string | null
  tipo: 'INI' | 'DINAMICO'
  ativo: boolean
  /// POWER_APPS: veio da migração e não se edita (a carga final reescreveria).
  origem: 'POWER_APPS' | 'SISTEMA'
  editavel: boolean
  campos: number
  inspecoes: number
  criadoEm: string
  atualizadoEm: string
}

export interface CampoDoChecklistAdmin {
  id: string
  campoCatalogoId: string | null
  nome: string
  tipoCampo: TipoCampo
  opcoes: string[]
  secao: string | null
  itemCode: string | null
  obrigatorio: boolean
  ordem: number
}

export interface ChecklistDetalheAdmin extends Omit<ChecklistAdmin, 'campos'> {
  descricao: string | null
  campos: CampoDoChecklistAdmin[]
}

export interface CampoCatalogoAdmin {
  id: string
  codigo: string
  texto: string
  tipoCampo: TipoCampo
  opcoes: string[]
  impeditivo: boolean
  /// Em quantos checklists ativos a pergunta já está.
  usos: number
}

export interface CorpoChecklist {
  nome: string
  codigo?: string | null
  categoria?: string | null
  revisao?: string | null
  descricao?: string | null
  campos: { id?: string; campoCatalogoId: string; secao?: string | null; itemCode?: string | null; obrigatorio: boolean }[]
  motivo?: string
  /// Na edição: a versão que a tela leu. O servidor recusa se alguém salvou depois.
  atualizadoEm?: string
}

export type AcaoAdmin =
  | 'CHECKLIST_CRIADO'
  | 'CHECKLIST_ALTERADO'
  | 'CHECKLIST_DESATIVADO'
  | 'CHECKLIST_REATIVADO'
  | 'PERFIL_CONCEDIDO'
  | 'PERFIL_REMOVIDO'
  | 'ACESSO_CONCEDIDO'
  | 'ACESSO_REVOGADO'
  | 'USUARIO_DESATIVADO'
  | 'USUARIO_REATIVADO'
  | 'USUARIO_EXCLUIDO'
  | 'USUARIO_CRIADO'
  | 'OBRAS_ALTERADAS'
  | 'SINCRONIZACAO_RM'
  | 'CONFIGURACAO_ALTERADA'
  | 'VOLTOU_A_SEGUIR_RM'

export interface EventoAdmin {
  id: string
  acao: AcaoAdmin
  alvoNome: string
  alvoEmail: string
  detalhes: {
    motivo?: string
    total?: number
    tentativaDeExclusao?: boolean
    perfis?: string[]
    /// 'grupo' quando a ação veio do grupo de acesso do Entra ID, no login.
    origem?: string
    obras?: string[]
    incluidas?: string[]
    removidas?: string[]
  } | null
  em: string
  autor: { id: string; nome: string; email: string }
}

export interface RespostaAcoesAdmin {
  temMais: boolean
  proximoCursor: string | null
  acoes: EventoAdmin[]
}

function montarQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== false) q.set(k, String(v))
  }
  return q.toString()
}

export type PerfilNome =
  | 'SUPERADMIN'
  | 'ADMIN'
  | 'GERENTE_CONTRATO'
  | 'ENGENHEIRO_SEGURANCA'
  | 'TECNICO_SEGURANCA'
  | 'ENCARREGADO'
  | 'COLABORADOR'
  | 'RESPONSAVEL_NC'

export interface UsuarioLinha {
  id: string
  nome: string
  email: string
  ativo: boolean
  acessoLiberado: boolean
  ultimoLogin: string | null
  perfis: PerfilNome[]
  obras: number
  inspecoes: number
}

export interface RespostaUsuarios {
  total: number
  pagina: number
  porPagina: number
  paginas: number
  usuarios: UsuarioLinha[]
}

export interface ResumoUsuarios {
  total: number
  comAcesso: number
  soCadastro: number
  jaEntraram: number
  porPerfil: Partial<Record<PerfilNome, number>>
  /// Nome do grupo do Entra ID que decide quem entra; null quando é o cadastro.
  grupoAcesso: string | null
}

export interface Aparelho {
  id: string
  descricao: string | null
  criadoEm: string
  expiraEm: string
  ultimoUso: string | null
  revogadoEm: string | null
}

export interface UsuarioDetalhe {
  id: string
  nome: string
  email: string
  ativo: boolean
  acessoLiberado: boolean
  entraOid: string | null
  ultimoLogin: string | null
  criadoEm: string
  /// Ajustados à mão: a sincronização com o RM não mexe.
  perfisManuais: boolean
  obrasManuais: boolean
  perfis: PerfilNome[]
  obras: { id: string; codigo: string; nome: string; fonte: string | null }[]
  dispositivos: Aparelho[]
}

export type AcaoAuditoria =
  | 'CRIACAO'
  | 'EDICAO'
  | 'EXCLUSAO'
  | 'MUDANCA_STATUS'
  | 'REVISAO_CRITICIDADE'

export interface EventoAuditoria {
  id: string
  acao: AcaoAuditoria
  alteracoes: Record<string, { de?: unknown; para?: unknown }> | null
  em: string
  usuario: { id: string; nome: string; email: string }
  inspecao: {
    id: string
    codigo: string
    status: string
    obra: { codigo: string; nome: string }
  }
}

export interface RespostaAuditoria {
  temMais: boolean
  proximoCursor: string | null
  eventos: EventoAuditoria[]
}

export interface RespostaAcessos {
  total: number
  pagina: number
  paginas: number
  acessos: (Aparelho & { usuario: { id: string; nome: string; email: string } })[]
}

/// `navigator.onLine` mente: Wi-Fi da obra conectado num roteador sem internet
/// reporta "online". A verdade é se o servidor respondeu.
export async function servidorAlcancavel(): Promise<boolean> {
  if (!navigator.onLine) return false
  try {
    await api.saude()
    return true
  } catch {
    return false
  }
}

// ------------------------------------------------------------ integração com o RM

export interface TotaisRm {
  novas: number
  pessoasAlteradas: number
  obrasIncluidas: number
  obrasRetiradas: number
  perfisIncluidos: number
  perfisRetirados: number
  sairamDoRm: number
  usuariosComAjusteManual: number
}

export interface MudancaRm {
  usuarioId: string
  email: string
  nome: string
  incluirPerfis: PerfilNome[]
  retirarPerfis: PerfilNome[]
  incluirObrasCodigos: string[]
  retirarObrasCodigos: string[]
  saiuDoRm: boolean
}

export interface ExecucaoRm {
  id: string
  recebidoEm: string
  total: number
  pessoasNoRm: number
  totais: TotaisRm
  ignorados: {
    semEmail: number
    semFuncao: number
    obrasDesconhecidas: string[]
    emailsEmVariasMatriculas: number
    emailsCompartilhados: string[]
  }
  avisos: string[]
  aplicadaEm: string | null
  aplicadaPor: string | null
  novas?: { email: string; nome: string; perfis: PerfilNome[]; obrasCodigos: string[] }[]
  mudancas?: MudancaRm[]
}

export interface EstadoIntegracaoRm {
  modo: 'previa' | 'automatico'
  configurada: boolean
  /// Ausente em servidor anterior a 15/10/2026.
  leituraDireta?: {
    configurada: boolean
    intervaloMin: number
    ultima: { em: string; resultado: 'mudou' | 'sem_mudanca' | 'erro'; total: number | null; erro: string | null } | null
  }
  regras: { perfil: PerfilNome; quando: string[][] }[]
  ultima: ExecucaoRm | null
  historico: ExecucaoRm[]
}
